import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, FormControl, Select, MenuItem,
  CircularProgress, Alert, Tooltip, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider
} from '@mui/material';
import { Search, Refresh, Description, Receipt, CheckCircle, Cancel, HourglassEmpty, Download, ChevronLeft, ChevronRight, Visibility, Close } from '@mui/icons-material';
import {
  getAdminPayoutReports as getPayoutReports,
  getAdminQrTransactionReports,
  getAdminUpiAtmReports,
  getAdminCreditCardReports,
  getAdminBbpsReports,
  getAdminEpBbpsReports,
  getAdminApiLogsReport,
  getAdminApiLogDetail,
  getAdminAepsReports
} from '../../services/api';
import { showWarningToast, showErrorToast, showSuccessToast } from '../../utils/toast';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateUtils';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount ?? 0);

// Status chip
const StatusChip = ({ status }) => {
  const map = {
    SUCCESS: { color: '#166534', bg: '#dcfce7', icon: <CheckCircle sx={{ fontSize: 12 }} /> },
    FAILED: { color: '#991b1b', bg: '#fee2e2', icon: <Cancel sx={{ fontSize: 12 }} /> },
    REVERSED: { color: '#86198f', bg: '#fdf4ff', icon: <Cancel sx={{ fontSize: 12 }} /> },
    REJECTED: { color: '#991b1b', bg: '#fee2e2', icon: <Cancel sx={{ fontSize: 12 }} /> },
    INITIATED: { color: '#92400e', bg: '#fef3c7', icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
    PENDING: { color: '#92400e', bg: '#fef3c7', icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
    SETTLEMENT_PENDING: { color: '#92400e', bg: '#fef3c7', icon: <HourglassEmpty sx={{ fontSize: 12 }} /> },
    SETTLED: { color: '#166534', bg: '#dcfce7', icon: <CheckCircle sx={{ fontSize: 12 }} /> },
    EXPIRED: { color: '#991b1b', bg: '#fee2e2', icon: <Cancel sx={{ fontSize: 12 }} /> },
  };
  const s = map[status?.toUpperCase()] || { color: '#374151', bg: '#f3f4f6', icon: null };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.5, py: 0.4, borderRadius: 99,
      backgroundColor: s.bg, color: s.color,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    }}>
      {s.icon}{status || 'N/A'}
    </Box>
  );
};

// Empty state
const Empty = ({ label }) => (
  <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
    <Receipt sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
    <Typography variant="body2">{label}</Typography>
  </Box>
);

// Table wrapper
const DataTable = ({ columns, rows, keyField = 'id' }) => (
  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
    <Table size="small" sx={{ minWidth: columns.length > 5 ? 950 : 650 }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: '#f9fafb' }}>
          {columns.map(c => (
            <TableCell key={c.key} sx={{ fontWeight: 600, fontSize: 11, letterSpacing: 0.5, color: '#4b5563', textTransform: 'uppercase', py: 1.5, ...(c.align ? { textAlign: c.align } : {}) }}>
              {c.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <TableRow key={row[keyField] ?? idx} hover sx={{ '&:last-child td': { border: 0 } }}>
            {columns.map(c => (
              <TableCell key={c.key} sx={{ fontSize: 13, py: 1.5, color: '#1f2937', ...(c.align ? { textAlign: c.align } : {}) }}>
                {c.render ? c.render(row) : (row[c.key] ?? '—')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const AdminServicesReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [serviceCode, setServiceCode] = useState('payout');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  const [reports, setReports] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const PAGE_SIZE = 10;

  const [merchantId, setMerchantId] = useState('');
  const [status, setStatus] = useState('ALL');
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const handleViewLogDetails = async (id) => {
    setDetailLoading(true);
    try {
      const response = await getAdminApiLogDetail(id);
      if (response && response.data) {
        setSelectedLog(response.data);
      } else {
        showErrorToast('Failed to load log details.');
      }
    } catch (err) {
      console.error(err);
      showErrorToast('Error retrieving api log details.');
    } finally {
      setDetailLoading(false);
    }
  };


  const fetchReport = async (targetPage = 1) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffTime = Math.abs(to - from);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (to < from) {
      const msg = 'To Date cannot be earlier than From Date.';
      setError(msg);
      showErrorToast(msg);
      return;
    }

    setLoading(true);
    setError('');

    try {
      let data = [];

      const handleResponse = (resData) => {
        if (resData?.status === 'FAILED' || resData?.status === 'error' || resData?.status === 'Failure') {
          const apiErrorMsg = resData.message || 'Failed to load reports.';
          setError(apiErrorMsg);
          showErrorToast(apiErrorMsg);
          setReports([]);
          setReportSummary(null);
          setTotalRecords(0);
          setTotalPages(0);
          setLoading(false);
          return null;
        }
        let d = [];
        if (resData && typeof resData === 'object' && !Array.isArray(resData)) {
          // Capture executive summary metrics if provided by backend
          if (resData.summary) {
            setReportSummary(resData.summary);
          } else {
            setReportSummary(null);
          }

          // Handle new nested QR response format (merchant + transaction structure)
          if (resData.data && Array.isArray(resData.data) && resData.data[0]?.merchant && resData.data[0]?.transaction) {
            d = resData.data.map(item => ({
              ...item.transaction,
              merchantId: item.merchant?.merchantId,
              merchantName: item.merchant?.merchantName,
              companyName: item.merchant?.companyName,
              settled: item.transaction?.settlementStatus === 'SETTLED'
            }));
            setTotalRecords(resData.totalRecords || d.length);
            setTotalPages(Math.ceil((resData.totalRecords || d.length) / PAGE_SIZE) || 1);
          } else {
            // Handle standard/epbbps/legacy flat array format
            d = (resData.transactions || resData.content || resData.data || []).map(item => ({
              ...item,
              merchantId: item.merchantId || item.merchant?.merchantId || resData.merchantId,
              merchantName: item.merchantName || item.merchant?.merchantName,
              companyName: item.companyName || item.merchant?.companyName
            }));
            setTotalRecords(resData.totalRecords || resData.total || resData.totalElements || d.length);
            setTotalPages(resData.totalPages || Math.ceil((resData.totalRecords || d.length) / PAGE_SIZE) || 1);
          }
        } else if (Array.isArray(resData)) {
          d = resData;
          setTotalRecords(d.length);
          setTotalPages(Math.ceil(d.length / PAGE_SIZE));
          setReportSummary(null);
        }
        return d;
      };

      if (serviceCode === 'payout') {
        const response = await getPayoutReports(fromDate, toDate, targetPage, PAGE_SIZE);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'qr') {
        const response = await getAdminQrTransactionReports(fromDate, toDate, targetPage, PAGE_SIZE);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'upiatm') {
        const response = await getAdminUpiAtmReports(fromDate, toDate, targetPage, PAGE_SIZE, merchantId, status);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'creditcard') {
        const response = await getAdminCreditCardReports(fromDate, toDate, targetPage, PAGE_SIZE, merchantId);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'bbps') {
        const response = await getAdminBbpsReports(fromDate, toDate, targetPage, PAGE_SIZE, merchantId);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'epbbps') {
        const response = await getAdminEpBbpsReports(fromDate, toDate, targetPage, PAGE_SIZE, merchantId, status);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'apilogs') {
        const response = await getAdminApiLogsReport(fromDate, toDate, targetPage, PAGE_SIZE, merchantId, status);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else if (serviceCode === 'aeps') {
        const response = await getAdminAepsReports(fromDate, toDate, targetPage, PAGE_SIZE, merchantId, status);
        const result = handleResponse(response.data);
        if (result === null) return;
        data = result;
      } else {
        data = [];
        setTotalRecords(0);
        setTotalPages(0);
      }

      setReports(data);
      setPage(targetPage);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      const apiErrorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to load reports. Please try again.';
      setError(apiErrorMsg);
      showErrorToast(apiErrorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Data only fetches when user explicitly clicks Fetch button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clear data when service changes (don't auto-fetch, just clear)
    setReports([]);
    setReportSummary(null);
    setPage(1);
    setTotalPages(0);
    setTotalRecords(0);
    setError('');
  }, [serviceCode]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      // Client-side pagination fallback if backend ignores pageSize
      if (reports.length > PAGE_SIZE) {
        setPage(newPage);
      } else {
        fetchReport(newPage);
      }
    }
  };

  const exportData = () => {
    if (!reports || reports.length === 0) {
      showWarningToast('No data to export. Please fetch the report first.');
      return;
    }

    const escapeCsv = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

    let headers, rows;
    if (serviceCode === 'qr') {
      headers = [
        'Date', 'Merchant Name', 'Merchant ID', 'Company Name',
        'Transaction ID', 'Order ID', 'Receipt', 'Payer VPA', 'Payer Name',
        'Amount', 'Charge', 'GST', 'Net Amount',
        'Status', 'Settlement Status', 'Customer Name', 'Customer Mobile'
      ];
      rows = reports.map(t => [
        formatDateTimeDDMMYYYY(t.createdAt || t.date),
        t.merchantName || '-',
        t.merchantId || '-',
        t.companyName || '-',
        t.transactionId || '-',
        t.orderId || t.receipt || '-',
        t.receipt || '-',
        t.payerVpa || '-',
        t.payerName || '-',
        t.amount || 0,
        t.chargeAmount || 0,
        t.gstAmount || 0,
        t.netAmount || 0,
        t.status || 'UNKNOWN',
        t.settlementStatus || 'UNKNOWN',
        t.customerName || t.payerName || '-',
        t.customerMobile || '-'
      ].map(escapeCsv));
    } else if (serviceCode === 'payout') {
      headers = [
        'Date', 'Merchant Name', 'Merchant ID', 'Service', 'Beneficiary Name', 'Bank Name', 'Account Number', 'IFSC',
        'Request ID', 'Transaction ID', 'UTR',
        'Base Amount', 'Charge Amount', 'GST Amount', 'Net Deduction',
        'Opening Balance', 'Closing Balance', 'Status'
      ];
      rows = reports.map(t => [
        formatDateTimeDDMMYYYY(t.createdAt || t.date),
        t.merchantName || '-',
        t.merchantId || '-',
        serviceCode || 'payout',
        t.beneficiaryName || '-',
        t.bankName || '-',
        t.accountNumber ? `\t${t.accountNumber}` : '-',
        t.ifscCode || '-',
        t.requestId || '-',
        t.transactionId || t.txnId || '-',
        t.status === 'SUCCESS' ? (t.utr || '-') : '-',
        t.amount || 0,
        t.chargeAmount || 0,
        t.gstAmount || 0,
        t.totalDeduction || 0,
        t.balanceBefore || 0,
        t.balanceAfter || 0,
        t.status || 'UNKNOWN'
      ].map(escapeCsv));
    } else if (serviceCode === 'aeps' || serviceCode === 'upiatm') {
      headers = [
        'Date', 'Merchant Name', 'Merchant ID', 'Transaction ID', 'Reference ID',
        'Base Amount', 'Charge Amount', 'GST Amount', 'Commission', 'TDS', 'Net Amount',
        'Status', 'Settlement Status'
      ];
      rows = reports.map(t => [
        formatDateTimeDDMMYYYY(t.createdAt || t.date),
        t.merchant?.merchantName || t.merchantName || '-',
        t.merchant?.merchantId || t.merchantId || '-',
        t.transactionId || '-',
        t.referenceId || '-',
        t.amount || t.baseAmount || 0,
        t.chargeAmount || 0,
        t.gstAmount || 0,
        t.commissionAmount || 0,
        t.tdsAmount || 0,
        t.netAmount || 0,
        t.status || t.transactionStatus || 'UNKNOWN',
        t.settlementStatus || 'UNKNOWN'
      ].map(escapeCsv));
    } else if (serviceCode === 'creditcard' || serviceCode === 'bbps') {
      headers = [
        'Date', 'Merchant Name', 'Merchant ID', 'Transaction ID', 'Reference ID', 'Type',
        'Base Amount', 'Charge Amount', 'GST Amount', 'Commission', 'TDS', 'Net Amount',
        'Opening Balance', 'Closing Balance', 'Status'
      ];
      rows = reports.map(t => [
        formatDateTimeDDMMYYYY(t.createdAt || t.date),
        t.merchant?.merchantName || t.merchantName || '-',
        t.merchant?.merchantId || t.merchantId || '-',
        t.transactionId || '-',
        t.referenceId || '-',
        t.transactionType || 'DEBIT',
        t.baseAmount || t.amount || 0,
        t.chargeAmount || 0,
        t.gstAmount || 0,
        t.commissionAmount || 0,
        t.tdsAmount || 0,
        t.netAmount || 0,
        t.openingBalance || 0,
        t.closingBalance || 0,
        t.status || 'UNKNOWN'
      ].map(escapeCsv));
    } else if (serviceCode === 'epbbps') {
      headers = [
        'Date & Time', 'Merchant Name', 'Merchant ID', 'Company Name',
        'Transaction ID', 'Order ID', 'Operator Ref / ID', 'Operator Code',
        'Service Type', 'API Code', 'Consumer / Mobile No', 'Consumer Name',
        'Base Amount', 'Charge Amount', 'GST Amount', 'Commission', 'Total Deduction',
        'Opening Balance', 'Closing Balance', 'Ledger ID', 'Source IP', 'Retry Count',
        'Status', 'Error Message'
      ];
      rows = reports.map(t => [
        formatDateTimeDDMMYYYY(t.createdAt || t.date || t.timestamp),
        t.merchantName || t.merchant?.merchantName || '-',
        t.merchantId || t.merchant?.merchantId || '-',
        t.companyName || t.merchant?.companyName || '-',
        t.transactionId || '-',
        t.orderId || '-',
        t.operatorId || '-',
        t.operatorCode || '-',
        t.serviceType || 'RECHARGE',
        t.apiCode || '-',
        t.consumerNo || t.mobileNumber || '-',
        t.consumerName || '-',
        t.amount || 0,
        t.chargeAmount || 0,
        t.gstAmount || 0,
        (t.amount != null && t.totalDeduction != null && t.amount > t.totalDeduction) ? Number((t.amount - t.totalDeduction).toFixed(2)) : 0,
        t.totalDeduction ?? t.amount ?? 0,
        t.balanceBefore ?? t.openingBalance ?? '-',
        t.balanceAfter ?? t.closingBalance ?? '-',
        t.ledgerId || '-',
        t.sourceIp || '-',
        t.retryCount ?? 0,
        t.status || 'UNKNOWN',
        t.errorMessage || '-'
      ].map(escapeCsv));
    } else if (serviceCode === 'apilogs') {
      headers = [
        'Date', 'Request ID', 'Merchant Name', 'Merchant ID', 'API Name', 'API Type',
        'API Endpoint', 'Source IP', 'Total Charge', 'GST Amount', 'Processing Time (ms)', 'Error Message', 'Status'
      ];
      rows = reports.map(t => [
        formatDateTimeDDMMYYYY(t.createdAt),
        t.requestId || '-',
        t.merchant?.merchantName || t.merchantName || '-',
        t.merchant?.merchantId || t.merchantId || '-',
        t.apiInfo?.apiName || t.apiCode || '-',
        t.apiInfo?.apiType || '-',
        t.apiInfo?.apiEndpoint || '-',
        t.sourceIp || '-',
        t.totalCharge || 0,
        t.gstAmount || 0,
        t.processingTimeMs || 0,
        t.errorMessage || '-',
        t.responseStatus || '-'
      ].map(escapeCsv));
    } else {
      headers = [];
      rows = [];
    }

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-services-report-${serviceCode}-${fromDate}-to-${toDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showSuccessToast('Report successfully exported!');
  };

  const getTableColumns = () => {
    if (serviceCode === 'qr') {
      return [
        { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt || r.date)} <br /> {new Date(r.createdAt || r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
        {
          key: 'merchant', label: 'Merchant', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.merchantName || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280' }}>{r.merchantId || '-'}</Typography>
            </Box>
          )
        },
        {
          key: 'refs', label: 'Order ID / Receipt', render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>Order: {r.orderId || r.receipt || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>Ref: {r.referenceId || '—'}</Typography>
            </Box>
          )
        },
        {
          key: 'payer', label: 'Payer Details', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.payerName || r.customerName || 'N/A'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#7c3aed', display: 'block' }}>{r.payerVpa || '—'}</Typography>
            </Box>
          )
        },
        { key: 'rrn', label: 'Txn ID / DigiKhata', render: r => <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#374151', fontWeight: 600 }}>{r.transactionId || '—'}</Typography> },
        {
          key: 'amount', label: 'Amount & Charges', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.amount)}</Typography>
              <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>Chg {fmt(r.chargeAmount)} + GST {fmt(r.gstAmount)}</Typography>
            </Box>
          )
        },
        { key: 'netAmount', label: 'Net Amount', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>{fmt(r.netAmount)}</Typography> },
        { key: 'txnStatus', label: 'Transaction Status', align: 'center', render: r => <StatusChip status={r.status} /> },
        { key: 'settlementStatus', label: 'Settlement Status', align: 'center', render: r => <StatusChip status={r.settlementStatus} /> },
      ];
    }

    if (serviceCode === 'payout') {
      return [
        { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt || r.date)} <br /> {new Date(r.createdAt || r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
        {
          key: 'merchant', label: 'Merchant', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.merchantName || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280' }}>{r.merchantId || '-'}</Typography>
            </Box>
          )
        },
        {
          key: 'beneficiary', label: 'Beneficiary', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.beneficiaryName || 'N/A'}</Typography>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>{r.bankName || 'No Bank Name'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#4b5563' }}>Acc: {r.accountNumber || '-'}</Typography>
            </Box>
          )
        },
        {
          key: 'transactionId', label: 'Req / Txn Details', render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>Req: {r.requestId || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>Txn: {r.transactionId || r.txnId || '—'}</Typography>
            </Box>
          )
        },
        { key: 'utr', label: 'UTR', render: r => <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#374151', fontWeight: 600 }}>{r.status === 'SUCCESS' ? (r.utr || '—') : '—'}</Typography> },
        {
          key: 'breakdown', label: 'Base & Charges', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.amount)}</Typography>
              <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>Chg {fmt(r.chargeAmount)} + GST {fmt(r.gstAmount)}</Typography>
            </Box>
          )
        },
        { key: 'netDeduction', label: 'Net Deduction', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#dc2626', fontSize: 13 }}>{fmt(r.totalDeduction)}</Typography> },
        { key: 'balanceBefore', label: 'Opening Balance', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>{fmt(r.balanceBefore)}</Typography> },
        { key: 'balanceAfter', label: 'Closing Balance', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>{fmt(r.balanceAfter)}</Typography> },
        { key: 'status', label: 'Status', align: 'right', render: r => <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}><StatusChip status={r.status} /></Box> },
      ];
    }

    if (serviceCode === 'aeps' || serviceCode === 'upiatm') {
      return [
        { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt || r.date)} <br /> {new Date(r.createdAt || r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
        {
          key: 'merchant', label: 'Merchant', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.merchant?.merchantName || r.merchantName || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280' }}>{r.merchant?.merchantId || r.merchantId || '-'}</Typography>
            </Box>
          )
        },
        {
          key: 'transactionId', label: 'Txn / Ref ID', render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>Txn: {r.transactionId || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>Ref: {r.referenceId || '—'}</Typography>
            </Box>
          )
        },
        {
          key: 'breakdown', label: 'Base & Charges', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.amount || r.baseAmount)}</Typography>
              <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>Chg {fmt(r.chargeAmount)} + GST {fmt(r.gstAmount)}</Typography>
            </Box>
          )
        },
        {
          key: 'commTds', label: 'Comm / TDS', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>+{fmt(r.commissionAmount)}</Typography>
              <Typography variant="caption" sx={{ color: '#dc2626', display: 'block' }}>TDS -{fmt(r.tdsAmount)}</Typography>
            </Box>
          )
        },
        { key: 'netAmount', label: 'Net Amount', align: 'right', render: r => <Typography sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>{fmt(r.netAmount)}</Typography> },
        { key: 'txnStatus', label: 'Status', align: 'center', render: r => <StatusChip status={r.status || r.transactionStatus} /> },
        { key: 'settlementStatus', label: 'Settlement Status', align: 'center', render: r => <StatusChip status={r.settlementStatus} /> },
      ];
    }

    if (serviceCode === 'creditcard' || serviceCode === 'bbps') {
      return [
        { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt || r.date)} <br /> {new Date(r.createdAt || r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
        {
          key: 'merchant', label: 'Merchant', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.merchant?.merchantName || r.merchantName || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280' }}>{r.merchant?.merchantId || r.merchantId || '-'}</Typography>
            </Box>
          )
        },
        {
          key: 'transactionId', label: 'Txn / Ref ID', render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>Txn: {r.transactionId || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>Ref: {r.referenceId || '—'}</Typography>
            </Box>
          )
        },
        {
          key: 'type', label: 'Type', render: r => (
            <Chip
              label={r.transactionType || 'DEBIT'}
              size="small"
              color={r.transactionType === 'CREDIT' ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontSize: 10, fontWeight: 700 }}
            />
          )
        },
        {
          key: 'breakdown', label: 'Base & Charges', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.baseAmount || r.amount)}</Typography>
              <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>Chg {fmt(r.chargeAmount)} + GST {fmt(r.gstAmount)}</Typography>
            </Box>
          )
        },
        {
          key: 'commTds', label: 'Comm / TDS', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#16a34a', fontSize: 13 }}>+{fmt(r.commissionAmount)}</Typography>
              <Typography variant="caption" sx={{ color: '#dc2626', display: 'block' }}>TDS -{fmt(r.tdsAmount)}</Typography>
            </Box>
          )
        },
        {
          key: 'netAmount', label: 'Net Amount', align: 'right', render: r => (
            <Typography sx={{ fontWeight: 600, color: r.transactionType === 'CREDIT' ? '#16a34a' : '#dc2626', fontSize: 13 }}>
              {fmt(r.netAmount)}
            </Typography>
          )
        },
        {
          key: 'balances', label: 'Balances (Op/Cl)', align: 'right', render: r => (
            <Box>
              <Typography variant="caption" sx={{ display: 'block' }}>Op: {fmt(r.openingBalance)}</Typography>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>Cl: {fmt(r.closingBalance)}</Typography>
            </Box>
          )
        },
        { key: 'status', label: 'Status', align: 'center', render: r => <StatusChip status={r.status || 'SUCCESS'} /> },
      ];
    }

    if (serviceCode === 'epbbps') {
      return [
        {
          key: 'createdAt',
          label: 'Date & Time',
          render: r => (
            <Typography variant="caption" sx={{ color: '#4b5563', whiteSpace: 'nowrap' }}>
              {formatDateDDMMYYYY(r.createdAt || r.date)} <br />
              {new Date(r.createdAt || r.date || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </Typography>
          )
        },
        {
          key: 'merchant',
          label: 'Merchant',
          render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>
                {r.merchantName || r.merchant?.merchantName || '—'}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>
                {r.merchantId || r.merchant?.merchantId || '-'}
              </Typography>
              {(r.companyName || r.merchant?.companyName) && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: 11 }}>
                  {r.companyName || r.merchant?.companyName}
                </Typography>
              )}
            </Box>
          )
        },
        {
          key: 'transactionId',
          label: 'Txn / Order / Op ID',
          render: r => (
            <Box>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937', display: 'block' }}>
                Txn: {r.transactionId || '—'}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280', display: 'block' }}>
                Order: {r.orderId || '—'}
              </Typography>
              {r.operatorId ? (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#2563eb', display: 'block' }}>
                  Op ID: {r.operatorId}
                </Typography>
              ) : null}
            </Box>
          )
        },
        {
          key: 'consumer',
          label: 'Consumer Details',
          render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>
                {r.consumerNo || r.mobileNumber || '—'}
              </Typography>
              {r.consumerName && (
                <Typography variant="caption" sx={{ color: '#4b5563', display: 'block' }}>
                  {r.consumerName}
                </Typography>
              )}
              {r.operatorCode && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: 11 }}>
                  Op Code: {r.operatorCode}
                </Typography>
              )}
            </Box>
          )
        },
        {
          key: 'serviceType',
          label: 'Service',
          render: r => (
            <Box>
              <Chip
                label={r.serviceType || 'RECHARGE'}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: 10, fontWeight: 700, borderRadius: 1 }}
              />
              {r.apiCode && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: 10, mt: 0.25 }}>
                  {r.apiCode}
                </Typography>
              )}
            </Box>
          )
        },
        {
          key: 'amount',
          label: 'Amount & Charges',
          align: 'right',
          render: r => {
            const comm = (r.amount != null && r.totalDeduction != null && r.amount > r.totalDeduction)
              ? (r.amount - r.totalDeduction)
              : 0;
            const charges = (r.chargeAmount || 0) + (r.gstAmount || 0);
            return (
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{fmt(r.amount)}</Typography>
                {comm > 0 ? (
                  <Typography variant="caption" sx={{ color: '#16a34a', display: 'block', fontWeight: 600 }}>
                    Comm: +{fmt(comm)}
                  </Typography>
                ) : charges > 0 ? (
                  <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>
                    Chg {fmt(r.chargeAmount || 0)} + GST {fmt(r.gstAmount || 0)}
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                    Chg: ₹0.00
                  </Typography>
                )}
              </Box>
            );
          }
        },
        {
          key: 'totalDeduction',
          label: 'Net Deduction',
          align: 'right',
          render: r => (
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#dc2626', fontSize: 13 }}>
                {fmt(r.totalDeduction ?? r.amount)}
              </Typography>
            </Box>
          )
        },
        {
          key: 'balances',
          label: 'Balances (Op/Cl)',
          align: 'right',
          render: r => (
            <Box>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Op: {r.balanceBefore !== undefined && r.balanceBefore !== null ? fmt(r.balanceBefore) : (r.openingBalance !== undefined ? fmt(r.openingBalance) : '—')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                Cl: {r.balanceAfter !== undefined && r.balanceAfter !== null ? fmt(r.balanceAfter) : (r.closingBalance !== undefined ? fmt(r.closingBalance) : '—')}
              </Typography>
            </Box>
          )
        },
        {
          key: 'status',
          label: 'Status',
          align: 'center',
          render: r => (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <StatusChip status={r.status || 'PENDING'} />
              {r.errorMessage && (
                <Tooltip title={r.errorMessage} arrow>
                  <Typography variant="caption" sx={{ color: '#dc2626', maxWidth: 120, display: 'block', cursor: 'pointer' }} noWrap>
                    {r.errorMessage}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          )
        },
        {
          key: 'actions',
          label: 'Action',
          align: 'center',
          render: r => (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedTxn(r)}
              startIcon={<Visibility sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none',
                borderRadius: 1.5,
                fontSize: 11,
                py: 0.3,
                px: 1,
                color: '#4f46e5',
                borderColor: '#4f46e5',
                '&:hover': { borderColor: '#4338ca', bgcolor: '#eef2ff' }
              }}
            >
              Details
            </Button>
          )
        },
      ];
    }

    if (serviceCode === 'apilogs') {
      return [
        { key: 'createdAt', label: 'Date', render: r => <Typography variant="caption" sx={{ color: '#4b5563' }}>{formatDateDDMMYYYY(r.createdAt)} <br /> {new Date(r.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</Typography> },
        { key: 'requestId', label: 'Request ID', render: r => <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#1f2937' }}>{r.requestId || '—'}</Typography> },
        {
          key: 'merchant', label: 'Merchant', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{r.merchant?.merchantName || r.merchantName || '—'}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6b7280' }}>{r.merchant?.merchantId || r.merchantId || '-'}</Typography>
            </Box>
          )
        },
        {
          key: 'apiName', label: 'API Details', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#4f46e5' }}>{r.apiInfo?.apiName || r.apiCode || '—'}</Typography>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>{r.apiInfo?.apiEndpoint || '—'}</Typography>
            </Box>
          )
        },
        { key: 'sourceIp', label: 'Source IP', render: r => <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.sourceIp || '—'}</Typography> },
        {
          key: 'charge', label: 'Cost & GST', align: 'right', render: r => (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{fmt(r.totalCharge)}</Typography>
              <Typography variant="caption" sx={{ color: '#ea580c', display: 'block' }}>GST {fmt(r.gstAmount)}</Typography>
            </Box>
          )
        },
        { key: 'responseStatus', label: 'Status', align: 'center', render: r => <StatusChip status={r.responseStatus} /> },
        {
          key: 'actions', label: 'Actions', align: 'center', render: r => (
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleViewLogDetails(r.id)}
              sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: 11, py: 0.2 }}
            >
              View Details
            </Button>
          )
        },
      ];
    }

    return [];
  };

  const startRecord = (page - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(page * PAGE_SIZE, totalRecords || reports.length);

  return (
    <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Admin Services Report</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Detailed breakdown of all service transactions across merchants
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={exportData}
            disabled={loading || reports.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#4f46e5', borderColor: '#4f46e5' }}
          >
            Export CSV
          </Button>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={() => fetchReport(page)}
              disabled={loading}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <Refresh sx={{ fontSize: 18, ...(loading && { animation: 'spin 1s linear infinite' }) }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filter Bar ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Description sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Report Filters</Typography>
          </Box>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>From Date</Typography>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  width: '100%', padding: '9px 12px',
                  border: '1px solid #e2e8f0', borderRadius: 8,
                  fontSize: 13, color: '#1e293b', outline: 'none',
                  fontFamily: 'inherit', backgroundColor: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>To Date</Typography>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  width: '100%', padding: '9px 12px',
                  border: '1px solid #e2e8f0', borderRadius: 8,
                  fontSize: 13, color: '#1e293b', outline: 'none',
                  fontFamily: 'inherit', backgroundColor: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Services</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={serviceCode}
                  onChange={e => setServiceCode(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: 2, fontSize: 13 }}
                >
                  <MenuItem value="payout">Payout</MenuItem>
                  <MenuItem value="qr">QR Pay-In (DigiKhata)</MenuItem>
                  <MenuItem value="aeps">AEPS</MenuItem>
                  <MenuItem value="upiatm">UPI-ATM</MenuItem>
                  <MenuItem value="creditcard">Credit Card</MenuItem>
                  {/* <MenuItem value="bbps">BBPS</MenuItem> */}
                  <MenuItem value="epbbps">BBPS</MenuItem>
                  <MenuItem value="apilogs">API Logs</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Merchant ID</Typography>
              <input
                type="text"
                placeholder="All Merchants"
                value={merchantId}
                onChange={e => setMerchantId(e.target.value)}
                style={{
                  width: '100%', padding: '9px 12px',
                  border: '1px solid #e2e8f0', borderRadius: 8,
                  fontSize: 13, color: '#1e293b', outline: 'none',
                  fontFamily: 'inherit', backgroundColor: '#fff',
                }}
              />
            </Grid>
            {['aeps', 'upiatm', 'apilogs', 'epbbps'].includes(serviceCode) && (
              <Grid item xs={12} sm={6} md={2}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Status</Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={status}
                    onChange={e => setStatus(e.target.value)}
                    sx={{ borderRadius: 2, fontSize: 13 }}
                  >
                    <MenuItem value="ALL">All Statuses</MenuItem>
                    {serviceCode === 'apilogs' ? (
                      <>
                        <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                        <MenuItem value="FAILED">FAILED</MenuItem>
                        <MenuItem value="PENDING">PENDING</MenuItem>
                      </>
                    ) : serviceCode === 'epbbps' ? (
                      <>
                        <MenuItem value="PENDING">PENDING</MenuItem>
                        <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                        <MenuItem value="FAILED">FAILED</MenuItem>
                        <MenuItem value="REVERSED">REVERSED</MenuItem>
                      </>
                    ) : (
                      <>
                        <MenuItem value="INITIATED">INITIATED</MenuItem>
                        <MenuItem value="PENDING">PENDING</MenuItem>
                        <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                        <MenuItem value="FAILED">FAILED</MenuItem>
                        <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                        <MenuItem value="REVERSED">REVERSED</MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={['aeps', 'upiatm', 'apilogs', 'epbbps'].includes(serviceCode) ? 2 : 4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Search sx={{ fontSize: 15 }} />}
                  onClick={() => fetchReport(1)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2.5, boxShadow: 'none' }}
                >
                  Fetch
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setFromDate(new Date().toISOString().split('T')[0]);
                    setToDate(new Date().toISOString().split('T')[0]);
                    setServiceCode('payout');
                    setMerchantId('');
                    setStatus('ALL');
                    setReports([]);
                    setReportSummary(null);
                    setPage(1);
                    setTotalPages(0);
                    setTotalRecords(0);
                  }}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Loading overlay */}
      {(loading || detailLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}



      {/* ── Report Data Table ── */}
      {!loading && reports.length > 0 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: '0 !important' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#faf5ff' }}>
              <Chip
                label={
                  serviceCode === 'qr' ? 'QR Pay-In' :
                    serviceCode === 'payout' ? 'Payout' :
                      serviceCode === 'aeps' ? 'AEPS' :
                        serviceCode === 'upiatm' ? 'UPI-ATM' :
                          serviceCode === 'creditcard' ? 'Credit Card' :
                            serviceCode === 'bbps' ? 'BBPS' :
                              serviceCode === 'epbbps' ? 'BBPS' : 'API Logs'
                }
                size="small"
                sx={{
                  bgcolor:
                    serviceCode === 'qr' ? '#7c3aed' :
                      serviceCode === 'payout' ? '#16a34a' :
                        serviceCode === 'aeps' ? '#0284c7' :
                          serviceCode === 'upiatm' ? '#ea580c' :
                            serviceCode === 'creditcard' ? '#4f46e5' :
                              serviceCode === 'bbps' ? '#e11d48' :
                                serviceCode === 'epbbps' ? '#0d9488' : '#64748b',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 11
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {
                  serviceCode === 'qr' ? 'DigiKhata QR Transaction Report — Admin View' :
                    serviceCode === 'payout' ? 'Payout Transactions Report — Admin View' :
                      serviceCode === 'aeps' ? 'AEPS Transactions Report — Admin View' :
                        serviceCode === 'upiatm' ? 'UPI-ATM Transactions Report — Admin View' :
                          serviceCode === 'creditcard' ? 'Credit Card Transactions Report — Admin View' :
                            serviceCode === 'bbps' ? 'BBPS Transactions Report — Admin View' :
                              serviceCode === 'epbbps' ? 'BBPS Transactions Report — Admin View' : 'API Logs Report — Admin View'
                }
              </Typography>
            </Box>
            <DataTable
              rows={reports.length > PAGE_SIZE ? reports.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : reports}
              columns={getTableColumns()}
            />
            {/* Pagination Controls */}
            {totalPages > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#f9fafb' }}>
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: 13 }}>
                  {totalRecords > 0 ? `Showing ${startRecord} to ${endRecord} of ${totalRecords}` : `Showing ${reports.length} records`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
                  >
                    <ChevronLeft fontSize="small" />
                  </IconButton>

                  {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => {
                    const p = i + 1;
                    if (totalPages <= 7 || p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
                      return (
                        <Button
                          key={p}
                          variant={p === page ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handlePageChange(p)}
                          sx={{
                            minWidth: 32, width: 32, height: 32, p: 0,
                            ...(p !== page && { border: '1px solid', borderColor: 'divider', color: '#374151', bgcolor: '#fff' }),
                            ...(p === page && { boxShadow: 'none' })
                          }}
                        >
                          {p}
                        </Button>
                      );
                    }
                    if (p === 2 || p === totalPages - 1) return <Typography key={p} sx={{ px: 0.5, color: '#9ca3af' }}>...</Typography>;
                    return null;
                  })}

                  <IconButton
                    size="small"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages || totalPages === 0}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
                  >
                    <ChevronRight fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      {!loading && reports.length === 0 && <Empty label="Click 'Fetch' to load data for the selected service" />}

      {/* ── API Log Detail Dialog ── */}
      <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>
          API Log Entry - {selectedLog?.requestId || 'Details'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>Request Information</Typography>
                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid', borderColor: '#e2e8f0', mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>API Name:</strong> {selectedLog.apiInfo?.apiName || '—'}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Endpoint:</strong> {selectedLog.apiInfo?.apiEndpoint || '—'}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>API Type:</strong> {selectedLog.apiInfo?.apiType || '—'}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Source IP:</strong> {selectedLog.sourceIp || '—'}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Processing Time:</strong> {selectedLog.processingTimeMs}ms</Typography>
                  <Typography variant="body2"><strong>Created At:</strong> {formatDateTimeDDMMYYYY(selectedLog.createdAt)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>Financial & Status</Typography>
                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid', borderColor: '#e2e8f0', mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Merchant:</strong> {selectedLog.merchant?.merchantName} ({selectedLog.merchant?.merchantId})</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Company:</strong> {selectedLog.merchant?.companyName || '—'}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Charge:</strong> {fmt(selectedLog.totalCharge)}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>GST:</strong> {fmt(selectedLog.gstAmount)}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Status:</strong> <StatusChip status={selectedLog.responseStatus} /></Typography>
                  {selectedLog.errorMessage && (
                    <Typography variant="body2" sx={{ color: '#dc2626', mt: 0.5 }}>
                      <strong>Error Message:</strong> {selectedLog.errorMessage}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Payload details */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>Merchant Request Payload</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: '#1e293b', color: '#f8fafc', fontFamily: 'monospace', fontSize: 12, overflowX: 'auto', maxHeight: 200 }}>
                  <pre style={{ margin: 0 }}>{JSON.stringify(selectedLog.requestPayload || {}, null, 2)}</pre>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>Merchant Response Payload</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: '#1e293b', color: '#f8fafc', fontFamily: 'monospace', fontSize: 12, overflowX: 'auto', maxHeight: 200 }}>
                  <pre style={{ margin: 0 }}>{JSON.stringify(selectedLog.responsePayload || {}, null, 2)}</pre>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>External Request Payload (sent to operator)</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: '#1e293b', color: '#f8fafc', fontFamily: 'monospace', fontSize: 12, overflowX: 'auto', maxHeight: 200 }}>
                  <pre style={{ margin: 0 }}>{JSON.stringify(selectedLog.externalRequestPayload || {}, null, 2)}</pre>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>External Response Payload (received from operator)</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: '#1e293b', color: '#f8fafc', fontFamily: 'monospace', fontSize: 12, overflowX: 'auto', maxHeight: 200 }}>
                  <pre style={{ margin: 0 }}>{JSON.stringify(selectedLog.externalResponsePayload || {}, null, 2)}</pre>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 1.5 }}>
          <Button onClick={() => setSelectedLog(null)} variant="contained" sx={{ textTransform: 'none', borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Transaction Details Dialog (Admin View) ── */}
      <Dialog
        open={!!selectedTxn}
        onClose={() => setSelectedTxn(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2.5, sm: 3 },
            width: '100%',
            maxWidth: 860,
            m: { xs: 1, sm: 2 },
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#ffffff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#4f46e5',
                border: '1px solid #c7d2fe'
              }}
            >
              <Receipt fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#0f172a', lineHeight: 1.2 }}>
                BBPS Transaction Details — Admin View
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.75rem' }}>
                ID: {selectedTxn?.transactionId}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedTxn && <StatusChip status={selectedTxn.status} />}
            <IconButton size="small" onClick={() => setSelectedTxn(null)} sx={{ color: '#94a3b8', '&:hover': { color: '#475569', bgcolor: '#f1f5f9' } }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2, overflowY: 'auto' }}>
          {selectedTxn && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, width: '100%' }}>
              {/* Summary 4-Metrics Cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                  gap: 1.25,
                  width: '100%'
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    bgcolor: '#f8fafc',
                    borderColor: '#e2e8f0',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.72rem', display: 'block', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Recharge Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.4, fontSize: '1.1rem', lineHeight: 1.2 }}>
                    {fmt(selectedTxn.amount)}
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    bgcolor: '#fffbeb',
                    borderColor: '#fef3c7',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600, fontSize: '0.72rem', display: 'block', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Charges + GST
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#d97706', mt: 0.4, fontSize: '1.1rem', lineHeight: 1.2 }}>
                    {fmt((selectedTxn.chargeAmount || 0) + (selectedTxn.gstAmount || 0))}
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    bgcolor: '#f0fdf4',
                    borderColor: '#bbf7d0',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 600, fontSize: '0.72rem', display: 'block', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Commission
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a', mt: 0.4, fontSize: '1.1rem', lineHeight: 1.2 }}>
                    {selectedTxn.amount > (selectedTxn.totalDeduction ?? selectedTxn.amount)
                      ? fmt(selectedTxn.amount - selectedTxn.totalDeduction)
                      : fmt(0)}
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    bgcolor: '#ecfdf5',
                    borderColor: '#a7f3d0',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#047857', fontWeight: 600, fontSize: '0.72rem', display: 'block', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Total Deduction
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669', mt: 0.4, fontSize: '1.1rem', lineHeight: 1.2 }}>
                    {fmt(selectedTxn.totalDeduction ?? selectedTxn.amount)}
                  </Typography>
                </Paper>
              </Box>

              {/* 2 Detail Columns */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.75,
                  width: '100%',
                  alignItems: 'stretch'
                }}
              >
                {/* Column 1: Merchant & Order Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.75, fontSize: '0.825rem' }}>
                    Merchant & Order Details
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#ffffff',
                      borderColor: '#e2e8f0',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      gap: 0.6
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Merchant Name:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f172a', textAlign: 'right', wordBreak: 'break-word' }}>
                        {selectedTxn.merchantName || selectedTxn.merchant?.merchantName || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Merchant ID:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem', color: '#0f172a', textAlign: 'right' }}>
                        {selectedTxn.merchantId || selectedTxn.merchant?.merchantId || '—'}
                      </Typography>
                    </Box>
                    {(selectedTxn.companyName || selectedTxn.merchant?.companyName) && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Company Name:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f172a', textAlign: 'right', wordBreak: 'break-word' }}>
                          {selectedTxn.companyName || selectedTxn.merchant?.companyName}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Order ID:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem', color: '#0f172a', textAlign: 'right' }}>
                        {selectedTxn.orderId || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Service Type:</Typography>
                      <Chip
                        label={selectedTxn.serviceType || 'RECHARGE'}
                        size="small"
                        sx={{ fontSize: 10, height: 20, fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>API Code:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', fontFamily: 'monospace', textAlign: 'right' }}>
                        {selectedTxn.apiCode || '—'}
                      </Typography>
                    </Box>
                    {selectedTxn.transactionType && selectedTxn.transactionType !== selectedTxn.apiCode && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Txn Type:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', textAlign: 'right' }}>
                          {selectedTxn.transactionType}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Created At:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 500, textAlign: 'right' }}>
                        {formatDateTimeDDMMYYYY(selectedTxn.createdAt)}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>

                {/* Column 2: Operator & Technical Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.75, fontSize: '0.825rem' }}>
                    Operator & Technical Details
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#ffffff',
                      borderColor: '#e2e8f0',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      gap: 0.6
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Transaction ID:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem', color: '#0f172a', textAlign: 'right' }}>
                        {selectedTxn.transactionId || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Consumer / Mobile:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a', textAlign: 'right' }}>
                        {selectedTxn.consumerNo || selectedTxn.mobileNumber || '—'}
                      </Typography>
                    </Box>
                    {selectedTxn.consumerName && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Consumer Name:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a', textAlign: 'right' }}>
                          {selectedTxn.consumerName}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Operator Ref / ID:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb', fontFamily: 'monospace', fontSize: '0.8rem', textAlign: 'right' }}>
                        {selectedTxn.operatorId || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Operator Code:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', textAlign: 'right' }}>
                        {selectedTxn.operatorCode || '—'}
                      </Typography>
                    </Box>
                    {selectedTxn.billValidationId && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Bill Validation ID:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', fontFamily: 'monospace', textAlign: 'right' }}>
                          {selectedTxn.billValidationId}
                        </Typography>
                      </Box>
                    )}
                    {selectedTxn.billDueDate && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Bill Due Date:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', textAlign: 'right' }}>
                          {formatDateTimeDDMMYYYY(selectedTxn.billDueDate)}
                        </Typography>
                      </Box>
                    )}
                    {selectedTxn.providerStatusCode && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Provider Status:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', fontFamily: 'monospace', textAlign: 'right' }}>
                          {selectedTxn.providerStatusCode}
                        </Typography>
                      </Box>
                    )}
                    {(selectedTxn.param1 || selectedTxn.param2) && [selectedTxn.param1, selectedTxn.param2].filter(p => p && p !== 'string').length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Request Params:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', textAlign: 'right' }}>
                          {[selectedTxn.param1, selectedTxn.param2].filter(p => p && p !== 'string').join(', ')}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Source IP:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', fontFamily: 'monospace', textAlign: 'right' }}>
                        {selectedTxn.sourceIp || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Retry Count:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', textAlign: 'right' }}>
                        {selectedTxn.retryCount ?? 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Updated At:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 500, textAlign: 'right' }}>
                        {formatDateTimeDDMMYYYY(selectedTxn.updatedAt || selectedTxn.createdAt)}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>

              {/* Wallet & Financial Breakdown (Full Width) */}
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.75, fontSize: '0.825rem' }}>
                  Wallet & Financial Breakdown
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#ffffff',
                    borderColor: '#e2e8f0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Opening Balance:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a', textAlign: 'right' }}>
                            {fmt(selectedTxn.balanceBefore ?? selectedTxn.openingBalance)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Recharge Base Amount:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a', textAlign: 'right' }}>
                            {fmt(selectedTxn.amount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Charges:</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', textAlign: 'right' }}>
                            {fmt(selectedTxn.chargeAmount || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>GST:</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#334155', textAlign: 'right' }}>
                            {fmt(selectedTxn.gstAmount || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                          <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600, fontSize: '0.78rem', flexShrink: 0 }}>Commission Earned:</Typography>
                          <Typography variant="body2" sx={{ color: '#166534', fontWeight: 700, fontSize: '0.82rem', textAlign: 'right' }}>
                            +{fmt(Math.max(0, (selectedTxn.amount || 0) - (selectedTxn.totalDeduction ?? selectedTxn.amount)))}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Closing Balance:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.82rem', textAlign: 'right' }}>
                            {fmt(selectedTxn.balanceAfter ?? selectedTxn.closingBalance)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, minHeight: 24 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0, fontWeight: 500 }}>Status:</Typography>
                          <StatusChip status={selectedTxn.status} />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 0.5 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: '#fef2f2',
                      py: 1,
                      px: 2,
                      borderRadius: 1.5,
                      border: '1px solid #fee2e2'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.85rem' }}>
                      Net Wallet Deduction:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#dc2626', fontSize: '0.95rem' }}>
                      {fmt(selectedTxn.totalDeduction ?? selectedTxn.amount)}
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              {/* Error Message if present */}
              {selectedTxn.errorMessage && (
                <Alert severity="error" sx={{ borderRadius: 2, py: 0.5, fontSize: '0.8rem', width: '100%' }}>
                  <strong>Operator Error Message:</strong> {selectedTxn.errorMessage}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.25, bgcolor: '#fafafa' }}>
          <Button
            onClick={() => setSelectedTxn(null)}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3.5,
              py: 0.65,
              fontSize: '0.875rem',
              fontWeight: 600,
              backgroundColor: '#4f46e5',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#4338ca', boxShadow: 'none' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminServicesReport;
